import { Autocomplete, Grid, Slider, Switch, TextField, Divider, MenuItem, ToggleButtonGroup, ToggleButton, Icon, Collapse, Tooltip } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { useAuth0 } from "@auth0/auth0-react";
import PropTypes from "prop-types";
import Swal from "sweetalert2";

import SoftBox from "components/SoftComponents/SoftBox";
import SoftButton from "components/SoftComponents/SoftButton";
import SoftInput from "components/SoftComponents/SoftInput";
import SoftTypography from "components/SoftComponents/SoftTypography";

import deviceAPI from "services/api/bks";
import groupAPI from "services/api/groups";

import { useSoftUIController } from "hooks/context";
import { useMemo } from "react";

// Reusable styles for SweetAlerts
const swalTitleStyle = "font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:28px";
const swalTextStyle = "font-family:Roboto,Helvetica,Arial,sans-serif; font-weight:500; font-size:20px";

function BKSGroupSettings({ selected_group }) {
    const auth0 = useAuth0();
    deviceAPI.loadToken(auth0);
    groupAPI.loadToken(auth0);

    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions } = controller;

    const showAdvancedDeviceLevel = userPermissions.includes('devices-advanced:view');
    const canAdvancedDeviceEdit = userPermissions.includes('devices-advanced:edit');

    const queryClient = useQueryClient();

    const device_types = [...new Set(selected_group?.devices?.map(d => d.device_type) || [])];

    // get device types for all devices in group, send through to api
    // use device types and check for skins that apply to all device types in req
    const deviceConfigs = useQuery({
        queryKey: ['getBKDeviceConfigs', device_types.join(','), selectedOrg],
        enabled: canAdvancedDeviceEdit,
        queryFn: async () => {
            return await deviceAPI.getBKDeviceConfigs(device_types.join(','));
        }
    });

    const defaultValues = {
        uuids: selected_group.uuids,
        organisation_id: selected_group.organisation_id,
        name: selected_group.name,
        color: selected_group.color,
        type: selected_group.type,

        liveview_type: null,
        behaviour: null,
        skin: null,
        firmware: null,
    };

    const memoizedConfigs = useMemo(() => {
        if (!deviceConfigs.data) {
            return {
                behaviours: [],
                skins: [],
                firmwares: []
            };
        }

        const firmwares = [];
        for (var [key, values] of Object.entries(deviceConfigs.data.firmwares)) {
            values.forEach(v => {
                firmwares.push({ category: key, label: v, value: v });
            });
        }

        const behaviours = deviceConfigs.data.behaviours.map(b => { return { value: b, label: b } })
        const skins = deviceConfigs.data.skins.map(s => { return { value: s, label: s } })

        return { behaviours, skins, firmwares };
    }, [deviceConfigs.data]);

    const {
        control,
        register,
        handleSubmit,
        setValue
    } = useForm({ defaultValues });

    const { mutateAsync, isPending } = useMutation({
        mutationFn: (values) => groupAPI.updateGroupDevicesConfig(selected_group.group_id, { ...values, canAdvancedDeviceEdit }),
        onMutate: () => {
            Swal.fire({
                title: `<div style="${swalTitleStyle}">Saving...</div>`,
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => Swal.showLoading()
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getGroupWithID", selected_group.group_id, selectedOrg] });
            queryClient.invalidateQueries({ queryKey: ['getAllGroups', selectedOrg] });

            Swal.fire({
                icon: 'success',
                title: `<div style="${swalTitleStyle}">Saved!</div>`,
                showConfirmButton: false,
                timer: 2000
            });
        },
        onError: (error) => {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: `<div style="${swalTitleStyle}">Error</div>`,
                html: `<div style="${swalTextStyle}">${error.message || "There was a problem saving your data. Ensure the input is unique."}</div>`,
            });
        }
    });

    const triggerGroupDevicesReboot = async () => {
        try {
            const response = await deviceAPI.rebootDevices(selected_group.uuids);
            if (!response) throw Error("Reboots failed");

            await Swal.fire({
                icon: "success",
                title: `<div style="${swalTitleStyle}">Devices Rebooting!</div>`,
                showConfirmButton: false,
                timer: 2000
            });
        } catch (error) {
            console.error(error);
            Swal.fire({
                icon: "error",
                title: `<div style="${swalTitleStyle}">Error</div>`,
                text: "There was a problem rebooting the devices.",
            });
        }
    };

    return (
        <SoftBox
            component="form"
            onSubmit={handleSubmit(mutateAsync)}
            style={{ height: deviceLayout === 'mobile' ? 'calc(100svh - 8.5rem - 16px)' : '100%' }}
        >
            <SoftBox display="flex" justifyContent="space-between" flexDirection="column" height="100%">
                <SoftBox>
                    <SoftBox px={2} py={1.75} display="flex" alignItems="center" sx={{ background: '#f9f9f9' }} >
                        <SoftTypography variant={deviceLayout === 'mobile' ? "h5" : "h4"}>
                            Group Configuration Settings
                        </SoftTypography>
                    </SoftBox>
                    <Divider />


                    <SoftBox m={2}>
                        <Divider />
                    </SoftBox>

                    <SoftBox px={2} my={1} display="flex" flexDirection="column" justifyContent="center">
                        <SoftTypography variant="body3" fontWeight="bold" pb={0.5}>NOTICE</SoftTypography>
                        <SoftTypography variant="caption">Any fields filled out within this form will be saved to <b>all devices in this group</b> if the 'Apply' button is used.</SoftTypography>
                    </SoftBox>

                    <SoftBox m={2} mb={0}>
                        <Divider />
                    </SoftBox>

                    {/* Basic Info */}
                    <Grid container spacing={2} px={2} pt={2} pb={1}>
                        <Grid item xxs={12} /*sm={6} xl={12} x3l={6}*/>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Group Name</SoftTypography>
                            </SoftBox>
                            <SoftInput placeholder="Device Name" {...register('name')} />
                        </Grid>
                    </Grid>

                    {/* Hardware Configurations (Behaviour, Liveview Type, Skin, Firmware) */}
                    <Grid container spacing={2} px={2} py={1}>
                        <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Behaviour</SoftTypography>
                            </SoftBox>
                            <Controller
                                name={'behaviour'}
                                control={control}
                                render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                                    const isOverride = value && !memoizedConfigs.behaviours.some(opt => opt.value === value);
                                    const shouldLock = isOverride && !canAdvancedDeviceEdit;

                                    return (
                                        <Autocomplete
                                            id="behaviour-autocomplete"
                                            disabled={shouldLock}
                                            freeSolo={showAdvancedDeviceLevel && canAdvancedDeviceEdit}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            options={memoizedConfigs.behaviours.map(opt => opt.value)}
                                            value={value || null}
                                            onChange={(event, newValue) => onChange(newValue)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={ref}
                                                    variant="outlined"
                                                    placeholder={showAdvancedDeviceLevel && canAdvancedDeviceEdit ? "Select or type override..." : "Select Behaviour..."}
                                                    error={!!error}
                                                    helperText={
                                                        shouldLock ? "Locked: Manual override detected. Admins only can edit."
                                                            : isOverride ? "Manual Override Active"
                                                                : error?.message || ""
                                                    }
                                                    sx={{
                                                        "& .MuiFormHelperText-root": {
                                                            color: isOverride ? "#ed6c02" : "inherit",
                                                            fontWeight: 600,
                                                        },
                                                        "& .MuiInputBase-root.Mui-disabled": {
                                                            backgroundColor: "rgba(0, 0, 0, 0.05)"
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Liveview Type</SoftTypography>
                            </SoftBox>
                            <Controller
                                name="liveview_type"
                                control={control}
                                render={({ field }) => (
                                    <ToggleButtonGroup
                                        color="primary"
                                        value={field.value ?? defaultValues.liveview_type}
                                        exclusive
                                        onChange={(event, newValue) => {
                                            console.log(newValue, field)
                                            if(newValue == field.value){
                                                field.onChange(null);
                                            }
                                            else {
                                                field.onChange(newValue);
                                            }
                                        }}
                                        aria-label="Liveview Type Toggle"
                                    >
                                        <ToggleButton
                                            value={'on'}
                                            sx={{
                                                px: 3,
                                                fontSize: '0.75rem !important',
                                                lineHeight: 1,
                                                borderTopLeftRadius: '8px',
                                                borderBottomLeftRadius: '8px',
                                                fontWeight: 'bold',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#6cb16e',
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: '#5a9e5d' }
                                                }
                                            }}
                                        >
                                            Live
                                        </ToggleButton>
                                        <ToggleButton
                                            value={'history'}
                                            sx={{
                                                px: 3,
                                                fontSize: '0.75rem !important',
                                                lineHeight: 1,
                                                borderTopRightRadius: '8px',
                                                borderBottomRightRadius: '8px',
                                                fontWeight: 'bold',
                                                '&.Mui-selected': {
                                                    backgroundColor: '#526d9e',
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: '#3d638f' }
                                                }
                                            }}
                                        >
                                            History
                                        </ToggleButton>
                                    </ToggleButtonGroup>
                                )}
                            />
                        </Grid>
                        <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Skin</SoftTypography>
                            </SoftBox>
                            <Controller
                                name={'skin'}
                                control={control}
                                render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                                    const isOverride = value && !memoizedConfigs.skins.some(opt => opt.value === value);
                                    const shouldLock = isOverride && !canAdvancedDeviceEdit;

                                    return (
                                        <Autocomplete
                                            id="skin-autocomplete"
                                            disabled={shouldLock}
                                            freeSolo={showAdvancedDeviceLevel && canAdvancedDeviceEdit}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            options={memoizedConfigs.skins.map(opt => opt.value)}
                                            value={value || null}
                                            onChange={(event, newValue) => onChange(newValue)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={ref}
                                                    variant="outlined"
                                                    placeholder={showAdvancedDeviceLevel && canAdvancedDeviceEdit ? "Select or type override..." : "Select Skin..."}
                                                    error={!!error}
                                                    helperText={
                                                        shouldLock ? "Locked: Manual override detected. Admins only can edit."
                                                            : isOverride ? "Manual Override Active"
                                                                : error?.message || ""
                                                    }
                                                    sx={{
                                                        "& .MuiFormHelperText-root": {
                                                            color: isOverride ? "#ed6c02" : "inherit",
                                                            fontWeight: 600,
                                                        },
                                                        "& .MuiInputBase-root.Mui-disabled": {
                                                            backgroundColor: "rgba(0, 0, 0, 0.05)"
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xxs={12} sm={6} xl={12} x3l={6}>
                            <SoftBox pl={1} my={0.5}>
                                <SoftTypography variant="body2" fontWeight='bold'>Firmware</SoftTypography>
                            </SoftBox>
                            <Controller
                                name={'firmware'}
                                control={control}
                                render={({ field: { onChange, value, ref }, fieldState: { error } }) => {
                                    const isOverride = value && !memoizedConfigs.firmwares.some(opt => opt.value === value);
                                    const shouldLock = isOverride && !canAdvancedDeviceEdit;

                                    return (
                                        <Autocomplete
                                            id="firmware-autocomplete"
                                            disabled={/*shouldLock*/ true}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            groupBy={(opt) => opt.category.replace('_', ' ')}
                                            options={memoizedConfigs.firmwares}
                                            value={value || null}
                                            onChange={(event, newValue) => onChange(newValue)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    inputRef={ref}
                                                    variant="outlined"
                                                    placeholder={"Select Firmware..."}
                                                    error={!!error}
                                                    sx={{
                                                        "& .MuiFormHelperText-root": {
                                                            color: isOverride ? "#ed6c02" : "inherit",
                                                            fontWeight: 600,
                                                        },
                                                        "& .MuiInputBase-root.Mui-disabled": {
                                                            backgroundColor: "rgba(0, 0, 0, 0.08) !important"
                                                        }
                                                    }}
                                                />
                                            )}
                                        />
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>
                </SoftBox>

                <SoftBox>
                    <Divider />
                    <SoftBox sx={{ background: '#f9f9f9' }} display='flex' flexDirection='row' justifyContent='space-between' gap={1} p={2}>
                        <SoftButton type="submit" variant="gradient" color="dark" disabled={isPending}>
                            Apply to Devices
                        </SoftButton>
                        <SoftButton variant="gradient" color="dark" onClick={triggerGroupDevicesReboot}>
                            Reboot Devices
                        </SoftButton>
                    </SoftBox>
                </SoftBox>
            </SoftBox>
        </SoftBox>
    );
}

BKSGroupSettings.propTypes = {
    selected_group: PropTypes.object.isRequired
};

export default BKSGroupSettings;